// frontend/src/utils/assetAccess.js
import { getTodayInSingaporeTimezone } from './dateUtils';

export const SOFTWARE_LICENSE_CATEGORY_CODE = 'LIC';
export const ACCESS_CONTROL_CATEGORY_CODE = 'ACC';
export const FACIAL_RECOGNITION_TYPE_CODE = 'HKV';

/**
 * Resolve category code from an asset row or a pending-return transfer.
 *
 * List payloads expose `category_code`. Transfer `asset_detail` nests it
 * under `asset_type.category.code`.
 *
 * @param {object|null|undefined} entity
 * @returns {string|null}
 */
export const getAssetCategoryCode = (entity) => {
  if (!entity) return null;
  return (
    entity.category_code
    || entity.asset_type?.category?.code
    || entity.asset_detail?.category_code
    || entity.asset_detail?.asset_type?.category?.code
    || null
  );
};

/**
 * Whether the custodian can open Assign from the asset list.
 *
 * Available stock uses the normal path. A mobile line left Deployed is
 * already in use (site, device, or a holder we could not link). The
 * custodian assigns the person from that state instead of pretending
 * the SIM is back in the cupboard.
 *
 * @param {object|null|undefined} asset
 * @returns {boolean}
 */
export const canAssignAsset = (asset) => {
  if (!asset) return false;
  if (asset.status === 'available') return true;
  return getAssetCategoryCode(asset) === 'TEL' && asset.status === 'deployed';
};

/**
 * Resolve an asset type code from list, detail, or pending-transfer payloads.
 *
 * @param {object|null|undefined} entity
 * @returns {string|null}
 */
export const getAssetTypeCode = (entity) => {
  if (!entity) return null;
  return (
    entity.asset_type_code
    || entity.asset_type?.code
    || entity.asset_detail?.asset_type_code
    || entity.asset_detail?.asset_type?.code
    || null
  );
};

/**
 * Software seats are digital entitlements (Autodesk, Google Workspace, …).
 * Condition, photos, and shelf/location notes do not apply.
 *
 * @param {object|null|undefined} entity
 * @returns {boolean}
 */
export const isSoftwareLicenseAsset = (entity) => {
  if (!entity) return false;
  if (entity.is_software_license_asset === true) return true;
  return getAssetCategoryCode(entity) === SOFTWARE_LICENSE_CATEGORY_CODE;
};

/**
 * Whether this item has something physical to receive and inspect.
 *
 * Explicit API capability wins. Category/type fallbacks keep the UI safe
 * during rolling deployment while older API responses may still be cached.
 *
 * @param {object|null|undefined} entity
 * @returns {boolean}
 */
export const requiresPhysicalHandover = (entity) => {
  if (!entity) return true;
  if (typeof entity.requires_physical_handover === 'boolean') {
    return entity.requires_physical_handover;
  }
  if (typeof entity.asset_type?.requires_physical_handover === 'boolean') {
    return entity.asset_type.requires_physical_handover;
  }
  if (typeof entity.asset_detail?.requires_physical_handover === 'boolean') {
    return entity.asset_detail.requires_physical_handover;
  }
  if (typeof entity.asset_detail?.asset_type?.requires_physical_handover === 'boolean') {
    return entity.asset_detail.asset_type.requires_physical_handover;
  }
  if (isSoftwareLicenseAsset(entity)) return false;
  return !(
    getAssetCategoryCode(entity) === ACCESS_CONTROL_CATEGORY_CODE
    && getAssetTypeCode(entity) === FACIAL_RECOGNITION_TYPE_CODE
  );
};

/**
 * Digital access rights are revoked, not returned to a shelf or licence pool.
 *
 * @param {object|null|undefined} entity
 * @returns {boolean}
 */
export const usesAccessDeactivationWorkflow = (entity) => {
  if (!entity) return false;
  if (typeof entity.uses_access_deactivation_workflow === 'boolean') {
    return entity.uses_access_deactivation_workflow;
  }
  const isAccessControl = (
    entity.is_access_control_asset === true
    || getAssetCategoryCode(entity) === ACCESS_CONTROL_CATEGORY_CODE
  );
  return isAccessControl && !requiresPhysicalHandover(entity);
};

/**
 * Whether Return to Inventory is the correct lifecycle action.
 *
 * Physical assets and reusable digital entitlements return to inventory.
 * Pure digital access is handled by Deactivate Access instead.
 *
 * @param {object|null|undefined} entity
 * @returns {boolean}
 */
export const canReturnAssetToInventory = (entity) => (
  !usesAccessDeactivationWorkflow(entity)
);

const LEASE_RETURN_BLOCKED_STATUSES = new Set([
  'disposed',
  'scrapped',
  'sold',
  'written_off',
  'terminated',
  'deactivated',
  'lost',
  'returned_to_lessor',
  'pending_disposal',
  'pending_receipt',
]);

/**
 * Whether the custodian can hand this asset back to its lessor.
 *
 * Only leased units that are still in our custody. Company-owned assets
 * are disposed. A return already in flight, or a disposal request, has to
 * be finished first.
 *
 * @param {object|null|undefined} asset
 * @returns {boolean}
 */
export const canReturnToLessor = (asset) => {
  if (!asset || asset.ownership_type !== 'leased') return false;
  return !LEASE_RETURN_BLOCKED_STATUSES.has(asset.status);
};

/**
 * Suggested handover default when creating an asset type.
 *
 * Runtime behaviour always uses the saved `requires_physical_handover` flag.
 * This heuristic only pre-fills the catalog form so a custodian does not
 * inherit laptop rules for a new Autodesk seat or Hikvision access type.
 *
 * @param {{ categoryCode?: string, typeCode?: string, typeName?: string }} [input]
 * @returns {boolean}
 */
export const defaultRequiresPhysicalHandover = ({
  categoryCode = '',
  typeCode = '',
  typeName = '',
} = {}) => {
  const category = String(categoryCode || '').toUpperCase();
  const code = String(typeCode || '').toUpperCase();
  const name = String(typeName || '').toLowerCase();
  if (category === SOFTWARE_LICENSE_CATEGORY_CODE) return false;
  if (category === ACCESS_CONTROL_CATEGORY_CODE) {
    return !(code === FACIAL_RECOGNITION_TYPE_CODE || name.includes('facial'));
  }
  return true;
};

/**
 * Whether the one-click "Deactivate access" action should be offered for an asset.
 *
 * Pure digital access can be revoked whenever required. Physical/hybrid access
 * credentials retain the existing offboarding rule: surface the action on or
 * after the holder's last employment day.
 *
 * @param {object} asset - asset list/detail row (needs is_access_control_asset,
 *   status, deactivation_date, current_assignee_employment_end_date)
 * @returns {boolean}
 */
export const canDeactivateAccess = (asset) => {
  if (!asset?.is_access_control_asset) return false;
  // Already revoked — nothing to do.
  if (asset.status === 'deactivated' || asset.deactivation_date) return false;
  if (usesAccessDeactivationWorkflow(asset)) return true;
  const endDate = asset.current_assignee_employment_end_date;
  if (!endDate) return false;
  // Compare date-only in Singapore time; show on/after the last active day.
  return String(endDate).slice(0, 10) <= getTodayInSingaporeTimezone();
};
